/**
 * Password protection in the browser: the shared library operations with WebCrypto, and the
 * refusals worded for the person reading them.
 */
import type { MapDocument, SealedSession } from '@orbit-mind/domain';
import {
  protectLibraryMap,
  ProtectionRefused,
  randomIds,
  saveSealedDocument,
  unprotectLibraryMap,
  type MapEngine,
  type ProtectionRefusal,
} from '@orbit-mind/map-engine';
import type { MapId } from '@orbit-mind/shared-types';

import { browserCipher } from './cipher';
import { localStorageLayer } from './storage';

type Text2 = (en: string, pt: string) => string;

const newMapId = (): MapId => `map_${randomIds().nodeId()}` as MapId;

export function refusalWording(reason: ProtectionRefusal, t: Text2): string {
  switch (reason) {
    case 'already-protected':
      return t('This map is already protected.', 'Este mapa já é protegido.');
    case 'has-files':
      return t(
        'Remove the pictures, audio and files from this map first: they are stored outside the encryption.',
        'Remova primeiro as imagens, áudios e arquivos deste mapa: eles ficariam fora da criptografia.',
      );
    case 'wrong-password':
      return t('Wrong password.', 'Senha incorreta.');
    case 'damaged':
      return t(
        'This protected map could not be read.',
        'Não foi possível ler este mapa protegido.',
      );
  }
}

async function worded<T>(work: () => Promise<T>, t: Text2): Promise<T> {
  try {
    return await work();
  } catch (cause) {
    if (cause instanceof ProtectionRefused)
      throw new Error(refusalWording(cause.reason, t), { cause });
    throw cause;
  }
}

export function protectMap(mapId: MapId, password: string, hint: string, t: Text2): Promise<MapId> {
  const layer = localStorageLayer();
  return worded(
    () =>
      protectLibraryMap({
        journal: layer.journal,
        catalog: layer.catalog,
        mapId,
        password,
        hint,
        cipher: browserCipher,
        newMapId: newMapId(),
        newRootId: randomIds().nodeId(),
      }),
    t,
  );
}

export function unprotectMap(mapId: MapId, password: string, t: Text2): Promise<MapId> {
  const layer = localStorageLayer();
  return worded(
    () =>
      unprotectLibraryMap({
        journal: layer.journal,
        catalog: layer.catalog,
        mapId,
        password,
        cipher: browserCipher,
        newMapId: newMapId(),
      }),
    t,
  );
}

export function saveSealed(
  real: MapEngine,
  document: MapDocument,
  session: SealedSession,
): Promise<void> {
  return saveSealedDocument(real, document, session, browserCipher);
}
